import { DatePicker, Form, Select } from 'antd';
import { type Dayjs } from 'dayjs';
import { type ReactNode, useContext, useMemo } from 'react';
import { AuthContext } from '../../../../contexts/auth-context-provider';
import { type Customer } from '../../../../entities';
import { Roles } from '../../../../types';
import { type LocalDate } from '../../../../utils/local-date';
import { getEarliestOrderDate } from '../../../../utils/order-utils';

interface CustomerOrderFieldsProps {
  customers: Customer[];
  customer: Customer | undefined;
  orderDate: LocalDate | undefined;
  /** The customer and the date of an existing order can't be changed */
  isUpdating: boolean;
  onCustomerChange: (customerId: string) => void;
  onOrderDateChange: (date: Dayjs | null) => void;
}

/** The customer and the date the order is placed for */
const CustomerOrderFields = ({
  customers,
  customer,
  orderDate,
  isUpdating,
  onCustomerChange,
  onOrderDateChange,
}: CustomerOrderFieldsProps): ReactNode => {
  const { session } = useContext(AuthContext);
  const role = session?.claims.role;
  const customerOptions = useMemo(
    () => customers.map((cust) => ({ label: cust.name, value: String(cust.id) })),
    [customers]
  );

  return (
    <Form layout="vertical" colon={false} requiredMark={false}>
      <Form.Item
        label="Customer"
        // Customers place orders for themselves only
        hidden={role === Roles.ROLE_CUSTOMER_ADMIN || role === Roles.ROLE_CUSTOMER_STANDARD}
      >
        <Select
          showSearch
          value={customer?.id}
          options={customerOptions}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={onCustomerChange}
          disabled={isUpdating}
          style={{ maxWidth: 500, minWidth: 300 }}
        />
      </Form.Item>
      <Form.Item label="Order Date">
        <DatePicker
          allowClear={false}
          title="Order Date"
          value={orderDate?.toDayjs() ?? null}
          // Only HHC admins may order past the customer's deadline
          minDate={
            role !== Roles.ROLE_HHC_ADMIN
              ? getEarliestOrderDate(customer?.customerSetting)
              : undefined
          }
          format="YYYY MMMM, DD"
          style={{ width: '100%', maxWidth: 500 }}
          onChange={onOrderDateChange}
          disabled={isUpdating}
        />
      </Form.Item>
    </Form>
  );
};

export default CustomerOrderFields;
